'use client';

import { useScroll } from 'motion/react';
import Image from 'next/image';
import { ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';

import { imageLoaderForId } from '../../../../domains/Layout/Image/URL/ImageURL';
import { useCurrentSite } from '../../../../shared/providers/currentSite/useCurrentSite';

import style from './HowItWorksSection.module.css';
import { Slide } from './Slide/Slide';

export function HowItWorksSection() {
    const { formatMessage } = useIntl();
    const { currentSite } = useCurrentSite();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: wrapperRef,
        offset: ['start start', 'end end'],
    });

    const slides: Array<{
        title: string;
        number: string;
        description: string;
        imageSrc?: string;
        bannerContent: ReactNode;
        overlayImageUrl?: string;
    }> = [
        {
            title: formatMessage({ id: 'page.home.ConceptionSection.firstSlideTitle' }),
            number: '1',
            description: formatMessage({
                id: 'page.home.ConceptionSection.firstSlideSubtitle',
            }),
            bannerContent: (() => {
                return formatMessage(
                    {
                        id: 'page.home.conceptionSection.firstSlideBanner',
                    },
                    {
                        strong: (chunks) => {
                            return (
                                <span
                                    key={`emphasize-${String(chunks)}`}
                                    className={style.emphasize}
                                >
                                    {chunks}
                                </span>
                            );
                        },
                    },
                );
            })(),
            overlayImageUrl: `/home/${currentSite.lang}/first-slide-how-it-works-contain-faster.gif`,
        },
        {
            title: formatMessage({ id: 'page.home.ConceptionSection.secondSlideTitle' }),
            number: '2',
            description: formatMessage({
                id: 'page.home.ConceptionSection.secondSlideSubtitle',
            }),
            imageSrc: '1824928',
            bannerContent: (() => {
                return formatMessage(
                    {
                        id: 'page.home.conceptionSection.secondSlideBanner',
                    },
                    {
                        strong: (chunks) => {
                            return (
                                <span
                                    key={`emphasize-${String(chunks)}`}
                                    className={style.emphasize}
                                >
                                    {chunks}
                                </span>
                            );
                        },
                    },
                );
            })(),
            overlayImageUrl: `/home/${currentSite.lang}/second-slide-how-it-works-contain.gif`,
        },
        {
            title: formatMessage({ id: 'page.home.ConceptionSection.thirdSlideTitle' }),
            number: '3',
            description: formatMessage({
                id: 'page.home.ConceptionSection.thirdSlideSubtitle',
            }),
            imageSrc: '1824952',
            bannerContent: (() => {
                return formatMessage(
                    {
                        id: 'page.home.conceptionSection.thirdSlideBanner',
                    },
                    {
                        strong: (chunks) => {
                            return (
                                <span
                                    key={`emphasize-${String(chunks)}`}
                                    className={style.emphasize}
                                >
                                    {chunks}
                                </span>
                            );
                        },
                    },
                );
            })(),
            overlayImageUrl: `/home/${currentSite.lang}/third-slide-how-it-works-contain.gif`,
        },
    ];

    return (
        <section ref={wrapperRef} className={style.wrapper}>
            <div className={style.container}>
                <Image
                    className={style.backgroundImage}
                    src="1825190"
                    alt=""
                    fill
                    loader={imageLoaderForId({ aspectRatio: 16 / 9 })}
                    sizes="100vw"
                />
                <div className={style.imageOverlay} />

                <h2 className={style.sectionTitle}>
                    {formatMessage({ id: 'page.home.ConceptionSection.title' })}
                </h2>

                <div className={style.slidesContainer}>
                    {slides.map((slide, index) => (
                        <Slide
                            key={index}
                            title={slide.title}
                            number={slide.number}
                            description={slide.description}
                            imageSrc={slide.imageSrc}
                            index={index}
                            totalSlides={slides.length}
                            scrollYProgress={scrollYProgress}
                            bannerContent={slide.bannerContent}
                            overlayImageUrl={slide.overlayImageUrl}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
